// =============================================================================
// SIMULATEUR MAPRIME ADAPT' (MPA) - Module TEF uniquement
// Aide ANAH pour l'adaptation des logements PMR
// =============================================================================

import { useState, useEffect } from "react";
import {
  Calculator,
  Euro,
  Users,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Info,
  Accessibility,
  Home,
  ChevronDown,
  ChevronUp,
  FileText,
  Wrench
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { formatDateFR } from "../../utils/dateFormat";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Types de travaux d'adaptation PMR
const TRAVAUX_CATEGORIES = {
  salle_de_bain: {
    label: "Salle de bain",
    icon: "🚿",
    travaux: [
      { code: "douche_pmr", label: "Douche PMR (plain-pied)", description: "Douche à l'italienne accessible" },
      { code: "wc_adapte", label: "WC adapté", description: "WC rehaussé avec barres d'appui" },
      { code: "lavabo_pmr", label: "Lavabo PMR", description: "Lavabo accessible fauteuil roulant" }
    ]
  },
  accessibilite: {
    label: "Accessibilité",
    icon: "♿",
    travaux: [
      { code: "monte_escalier", label: "Monte-escalier", description: "Siège élévateur pour escalier" },
      { code: "rampe_acces", label: "Rampe d'accès", description: "Rampe pour accès fauteuil roulant" },
      { code: "elargissement_portes", label: "Élargissement portes", description: "Portes largeur PMR (90cm min)" },
      { code: "ascenseur_privatif", label: "Ascenseur privatif", description: "Élévateur vertical" }
    ]
  },
  domotique: {
    label: "Domotique",
    icon: "🏠",
    travaux: [
      { code: "volets_motorises", label: "Volets motorisés", description: "Volets roulants électriques" },
      { code: "chemin_lumineux", label: "Chemin lumineux", description: "Éclairage automatique nuit" },
      { code: "portier_video", label: "Portier vidéo", description: "Interphone vidéo connecté" }
    ]
  },
  securite: {
    label: "Sécurité",
    icon: "🛡️",
    travaux: [
      { code: "barres_appui", label: "Barres d'appui", description: "Barres de maintien murales" },
      { code: "sol_antiderapant", label: "Sol antidérapant", description: "Revêtement sécurisé" },
      { code: "nez_marches", label: "Nez de marches", description: "Contraste visuel escaliers" }
    ]
  }
};

// Niveaux GIR
const NIVEAUX_GIR = [
  { value: "gir_1", label: "GIR 1 - Dépendance totale", description: "Personne confinée au lit/fauteuil" },
  { value: "gir_2", label: "GIR 2 - Dépendance importante", description: "Fonctions mentales ou locomotrices altérées" },
  { value: "gir_3", label: "GIR 3 - Dépendance partielle", description: "Autonomie mentale mais aide corporelle" },
  { value: "gir_4", label: "GIR 4 - Aide ponctuelle", description: "Aide pour toilette, repas" },
  { value: "gir_5", label: "GIR 5 - Aide occasionnelle", description: "Aide pour ménage, repas" },
  { value: "gir_6", label: "GIR 6 - Autonome", description: "Personne autonome pour actes essentiels" },
  { value: "non_evalue", label: "Non évalué", description: "Pas encore d'évaluation GIR" }
];

export default function SimulateurMPA({ tenantId, dossierId, onSimulationComplete }) {
  const [loading, setLoading] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [checkingFeature, setCheckingFeature] = useState(true);
  const [travauxOpen, setTravauxOpen] = useState({});
  const [simulationResult, setSimulationResult] = useState(null);
  const [baremes, setBaremes] = useState(null);
  
  // Formulaire de simulation
  const [formData, setFormData] = useState({
    // Critères d'éligibilité (au moins un requis)
    age: "",
    gir: "",
    taux_incapacite: "",
    // Revenus
    rfr: "",
    nb_personnes: 1,
    region: "province",
    // Travaux
    montant_travaux_ht: "",
    types_travaux: []
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    const userId = localStorage.getItem('user_id');
    const userRole = localStorage.getItem('user_role');
    return {
      "X-Tenant-ID": tenantId || "tef",
      "Content-Type": "application/json",
      ...(userId && { "X-User-ID": userId }),
      ...(userRole && { "X-User-Role": userRole }),
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Vérifier si MPA est activé pour ce tenant
  useEffect(() => {
    checkFeatureFlag();
  }, [tenantId]);

  const checkFeatureFlag = async () => {
    setCheckingFeature(true);
    try {
      const res = await fetch(`${API}/api/metier/aides/feature-flags`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setFeatureEnabled(data.flags?.mpa_enabled || false);
        if (data.flags?.mpa_enabled) {
          loadBaremes();
        }
      }
    } catch (error) {
      console.error("Erreur vérification feature flag:", error);
    } finally {
      setCheckingFeature(false);
    }
  };

  // Charger les barèmes MPA
  const loadBaremes = async () => {
    try {
      const res = await fetch(`${API}/api/metier/aides/mpa/baremes`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setBaremes(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement barèmes:", error);
    }
  };

  // Vérification rapide d'éligibilité
  const checkEligibilite = async () => {
    try {
      const params = new URLSearchParams();
      if (formData.age) params.append('age', formData.age);
      if (formData.gir && formData.gir !== 'non_evalue') params.append('gir', formData.gir);
      if (formData.taux_incapacite) params.append('taux_incapacite', formData.taux_incapacite);
      
      const res = await fetch(`${API}/api/metier/aides/mpa/eligibilite/check?${params}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        return await res.json();
      }
      return null;
    } catch (error) {
      console.error("Erreur vérification éligibilité:", error);
      return null;
    }
  };

  // Lancer la simulation
  const runSimulation = async () => {
    // Validation
    if (!formData.age && !formData.gir && !formData.taux_incapacite) {
      toast.error("Au moins un critère d'éligibilité requis (âge, GIR ou taux d'incapacité)");
      return;
    }
    if (!formData.rfr) {
      toast.error("Veuillez indiquer le revenu fiscal de référence");
      return;
    }
    if (!formData.montant_travaux_ht) {
      toast.error("Veuillez indiquer le montant des travaux HT");
      return;
    }
    if (formData.types_travaux.length === 0) {
      toast.error("Veuillez sélectionner au moins un type de travaux");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        age: formData.age ? parseInt(formData.age) : null,
        gir: formData.gir && formData.gir !== 'non_evalue' ? formData.gir : null,
        taux_incapacite: formData.taux_incapacite ? parseInt(formData.taux_incapacite) : null,
        rfr: parseFloat(formData.rfr),
        nb_personnes: parseInt(formData.nb_personnes),
        region: formData.region,
        montant_travaux_ht: parseFloat(formData.montant_travaux_ht),
        types_travaux: formData.types_travaux
      };

      const res = await fetch(`${API}/api/metier/aides/mpa/simuler`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const result = await res.json();
        setSimulationResult(result);
        toast.success("Simulation MPA effectuée");
        if (onSimulationComplete) {
          onSimulationComplete(result);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la simulation");
      }
    } catch (error) {
      console.error("Erreur simulation MPA:", error);
      toast.error("Erreur de communication");
    } finally {
      setLoading(false);
    }
  };

  // Toggle travaux sélectionné
  const toggleTravaux = (code) => {
    setFormData(prev => ({
      ...prev,
      types_travaux: prev.types_travaux.includes(code)
        ? prev.types_travaux.filter(t => t !== code)
        : [...prev.types_travaux, code]
    }));
  };

  // Format monétaire
  const formatMontant = (val) => {
    if (val === null || val === undefined) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(val);
  };

  // Loading state
  if (checkingFeature) {
    return (
      <div className="flex items-center justify-center p-8">
        <Calculator className="w-6 h-6 animate-spin text-[var(--oh-success-text)]" />
        <span className="ml-2 text-[var(--oh-text-muted)]">Vérification des fonctionnalités...</span>
      </div>
    );
  }

  // Feature désactivée
  if (!featureEnabled) {
    return (
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[var(--oh-info-text)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--oh-text)] mb-2">
            Module MaPrimeAdapt' non disponible
          </h3>
          <p className="text-[var(--oh-text-muted)]">
            Ce simulateur est réservé aux workflows AMO (tenant TEF).
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="simulateur-mpa">
      {/* Header */}
      <Card className="bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border-[var(--oh-success-border)]">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[var(--oh-success-bg)] rounded-xl">
              <Accessibility className="w-8 h-8 text-[var(--oh-success-text)]" />
            </div>
            <div>
              <h2 className="text-xl font-medium text-[var(--oh-text)]">Simulateur MaPrimeAdapt'</h2>
              <p className="text-[var(--oh-text-muted)] text-sm">
                Aide ANAH pour l'adaptation du logement des personnes en perte d'autonomie
              </p>
            </div>
          </div>
          
          {/* Info éligibilité */}
          <div className="mt-4 p-4 bg-[var(--oh-bg-surface)] rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-[var(--oh-success-text)] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[var(--oh-text-muted)]">
                <p className="font-medium text-[var(--oh-success-text)] mb-1">Critères d'éligibilité (au moins un requis) :</p>
                <ul className="list-disc list-inside space-y-1 text-[var(--oh-text-muted)]">
                  <li>Âge ≥ 70 ans</li>
                  <li>GIR 1 à 6 évalué par un professionnel</li>
                  <li>Taux d'incapacité ≥ 50% (MDPH)</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader>
            <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--oh-success-text)]" />
              Informations du bénéficiaire
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Critères d'éligibilité */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] border-b border-[var(--oh-border)] pb-2">
                Critères d'éligibilité
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[var(--oh-text-muted)]">Âge du bénéficiaire</Label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    placeholder="70"
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                  <p className="text-xs text-[var(--oh-text-secondary)]">Éligible si ≥ 70 ans</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[var(--oh-text-muted)]">Taux d'incapacité (%)</Label>
                  <Input
                    type="number"
                    value={formData.taux_incapacite}
                    onChange={(e) => setFormData({...formData, taux_incapacite: e.target.value})}
                    placeholder="50"
                    min="0"
                    max="100"
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                  <p className="text-xs text-[var(--oh-text-secondary)]">MDPH - Éligible si ≥ 50%</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Niveau GIR</Label>
                <Select
                  value={formData.gir}
                  onValueChange={(v) => setFormData({...formData, gir: v})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                    <SelectValue placeholder="Sélectionner le niveau GIR" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    {NIVEAUX_GIR.map(g => (
                      <SelectItem key={g.value} value={g.value} className="text-[var(--oh-text)]">
                        <div>
                          <span>{g.label}</span>
                          <span className="text-[var(--oh-text-muted)] text-xs ml-2">{g.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Revenus */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] border-b border-[var(--oh-border)] pb-2">
                Revenus du foyer
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[var(--oh-text-muted)]">Revenu fiscal de référence *</Label>
                  <Input
                    type="number"
                    value={formData.rfr}
                    onChange={(e) => setFormData({...formData, rfr: e.target.value})}
                    placeholder="20000"
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[var(--oh-text-muted)]">Nombre de personnes</Label>
                  <Select
                    value={String(formData.nb_personnes)}
                    onValueChange={(v) => setFormData({...formData, nb_personnes: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[var(--oh-text)]">
                          {n} personne{n > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Région</Label>
                <Select
                  value={formData.region}
                  onValueChange={(v) => setFormData({...formData, region: v})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectItem value="idf" className="text-[var(--oh-text)]">Île-de-France</SelectItem>
                    <SelectItem value="province" className="text-[var(--oh-text)]">Province</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Travaux */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] border-b border-[var(--oh-border)] pb-2">
                Travaux d'adaptation
              </h4>
              
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Montant des travaux HT *</Label>
                <Input
                  type="number"
                  value={formData.montant_travaux_ht}
                  onChange={(e) => setFormData({...formData, montant_travaux_ht: e.target.value})}
                  placeholder="15000"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
                <p className="text-xs text-[var(--oh-text-secondary)]">Plafond : 22 000 € HT</p>
              </div>
              
              {/* Sélection des travaux par catégorie */}
              <div className="space-y-3">
                <Label className="text-[var(--oh-text-muted)]">Types de travaux *</Label>
                {Object.entries(TRAVAUX_CATEGORIES).map(([catKey, cat]) => (
                  <Collapsible
                    key={catKey}
                    open={travauxOpen[catKey]}
                    onOpenChange={(open) => setTravauxOpen({...travauxOpen, [catKey]: open})}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-[var(--oh-bg-surface)] rounded-lg hover:bg-[var(--oh-bg-surface)]">
                      <div className="flex items-center gap-2">
                        <span>{cat.icon}</span>
                        <span className="text-[var(--oh-text)] font-medium">{cat.label}</span>
                        <Badge variant="outline" className="border-[var(--oh-border)] text-[var(--oh-text-muted)]">
                          {cat.travaux.filter(t => formData.types_travaux.includes(t.code)).length} / {cat.travaux.length}
                        </Badge>
                      </div>
                      {travauxOpen[catKey] ? (
                        <ChevronUp className="w-4 h-4 text-[var(--oh-text-muted)]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[var(--oh-text-muted)]" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-2 pl-4">
                      {cat.travaux.map(t => (
                        <div
                          key={t.code}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            formData.types_travaux.includes(t.code)
                              ? 'bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]'
                              : 'bg-[var(--oh-bg-elevated)] hover:bg-[var(--oh-bg-elevated)]'
                          }`}
                          onClick={() => toggleTravaux(t.code)}
                        >
                          <Checkbox
                            checked={formData.types_travaux.includes(t.code)}
                            className="border-[var(--oh-border)]"
                          />
                          <div className="flex-1">
                            <p className="text-[var(--oh-text)] text-sm font-medium">{t.label}</p>
                            <p className="text-[var(--oh-text-muted)] text-xs">{t.description}</p>
                          </div>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>

            {/* Bouton simulation */}
            <Button
              onClick={runSimulation}
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="btn-simuler-mpa"
            >
              {loading ? (
                <Calculator className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Calculator className="w-4 h-4 mr-2" />
              )}
              Simuler MaPrimeAdapt'
            </Button>
          </CardContent>
        </Card>

        {/* Résultats */}
        <div className="space-y-6">
          {simulationResult ? (
            <>
              {/* Résultat principal */}
              <Card className={`border-2 ${
                simulationResult.eligible 
                  ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-[var(--oh-success-border)]'
                  : 'bg-gradient-to-br from-red-500/20 to-rose-500/20 border-[var(--oh-error-border)]'
              }`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    {simulationResult.eligible ? (
                      <CheckCircle className="w-10 h-10 text-[var(--oh-success-text)]" />
                    ) : (
                      <AlertTriangle className="w-10 h-10 text-[var(--oh-error-text)]" />
                    )}
                    <div>
                      <h3 className="text-xl font-medium text-[var(--oh-text)]">
                        {simulationResult.eligible ? 'Éligible MaPrimeAdapt\'' : 'Non éligible'}
                      </h3>
                      <p className="text-[var(--oh-text-muted)] text-sm">{simulationResult.motif_eligibilite}</p>
                    </div>
                  </div>
                  
                  {simulationResult.eligible && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4 text-center">
                        <p className="text-[var(--oh-text-muted)] text-sm">Catégorie revenus</p>
                        <p className="text-lg font-medium text-[var(--oh-text)] capitalize">
                          {simulationResult.categorie_revenus?.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4 text-center">
                        <p className="text-[var(--oh-text-muted)] text-sm">Taux de prise en charge</p>
                        <p className="text-2xl font-medium text-[var(--oh-success-text)]">
                          {(simulationResult.taux_prise_en_charge * 100).toFixed(0)}%
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Détails financiers */}
              {simulationResult.eligible && (
                <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  <CardHeader>
                    <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                      <Euro className="w-5 h-5 text-[var(--oh-success-text)]" />
                      Détail de l'aide
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-[var(--oh-border)]">
                      <span className="text-[var(--oh-text-muted)]">Montant travaux HT</span>
                      <span className="text-[var(--oh-text)] font-medium">
                        {formatMontant(simulationResult.montant_travaux_ht)}
                      </span>
                    </div>
                    
                    {simulationResult.montant_travaux_ht > simulationResult.montant_travaux_plafonnes && (
                      <div className="flex justify-between items-center py-2 border-b border-[var(--oh-border)]">
                        <span className="text-[var(--oh-text-muted)]">Montant plafonné</span>
                        <span className="text-[var(--oh-info-text)] font-medium">
                          {formatMontant(simulationResult.montant_travaux_plafonnes)}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center py-2 border-b border-[var(--oh-border)]">
                      <span className="text-[var(--oh-text-muted)]">Taux MaPrimeAdapt'</span>
                      <span className="text-[var(--oh-success-text)] font-medium">
                        {(simulationResult.taux_prise_en_charge * 100).toFixed(0)}%
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 bg-[var(--oh-success-bg)] rounded-lg px-3">
                      <span className="text-[var(--oh-text)] font-medium">Montant aide MPA</span>
                      <span className="text-2xl font-medium text-[var(--oh-success-text)]">
                        {formatMontant(simulationResult.montant_aide_mpa)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 bg-[var(--oh-bg-surface)] rounded-lg px-3">
                      <span className="text-[var(--oh-text-muted)] font-medium">Reste à charge</span>
                      <span className="text-xl font-medium text-[var(--oh-text)]">
                        {formatMontant(simulationResult.reste_a_charge)}
                      </span>
                    </div>
                    
                    {/* Avertissements */}
                    {simulationResult.avertissements?.length > 0 && (
                      <div className="mt-4 p-3 bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)] rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-[var(--oh-info-text)] flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-[var(--oh-info-text)]">
                            {simulationResult.avertissements.map((a, i) => (
                              <p key={i}>{a}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Travaux sélectionnés */}
              <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                <CardHeader>
                  <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-[var(--oh-success-text)]" />
                    Travaux sélectionnés
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {simulationResult.types_travaux_eligibles?.map(code => {
                      // Trouver le label du travaux
                      let label = code;
                      Object.values(TRAVAUX_CATEGORIES).forEach(cat => {
                        const t = cat.travaux.find(tr => tr.code === code);
                        if (t) label = t.label;
                      });
                      return (
                        <Badge key={code} className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-[var(--oh-success-border)]">
                          {label}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Info barème */}
              <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-[var(--oh-text-muted)] text-xs">
                    <FileText className="w-4 h-4" />
                    <span>
                      Barème : {simulationResult.bareme_version} | 
                      Simulation du {formatDateFR(simulationResult.date_simulation)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            /* État initial */
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardContent className="p-8 text-center">
                <Accessibility className="w-16 h-16 text-[var(--oh-text-secondary)] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[var(--oh-text-muted)] mb-2">
                  Lancez une simulation
                </h3>
                <p className="text-sm text-[var(--oh-text-secondary)]">
                  Remplissez le formulaire et cliquez sur "Simuler" pour estimer l'aide MaPrimeAdapt'.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
